import React, { useCallback, useState } from 'react';
import DataRoomNdaTableContext from './DataRoomNdaTableProvider.context';
import {
  DATA_ROOM_NDA_SIGNING_TIMELINE_ACTION,
  DataRoomNdaSigner,
  DataRoomNdaSigning,
} from 'router/subrouters/DataRoom/models/DataRoomNDA';
import DataRoomNdaSignerDrawer from '../../components/DataRoomNdaSignerDrawer';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { getSignerInfo } from '../../components/DataRoomNdasTable/components';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import DataRoomChooseNdaModal from '../../components/DataRoomChooseNdaModal';

type DataRoomNdaTableProviderProps = {
  signers: DataRoomNdaSigner[];
  updateSigner: (signer: Partial<DataRoomNdaSigner>) => void;
};

const DataRoomNdaTableProvider: React.FC<DataRoomNdaTableProviderProps> = (
  props,
) => {
  const { children, updateSigner } = props;

  const [selectedSigner, setSelectedSigner] = useState<DataRoomNdaSigner>(null);
  const [selectedSigning, setSelectedSigning] =
    useState<DataRoomNdaSigning>(null);

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);
  const [modal, modalRef] = useCallbackRef<OverlayRef>();

  const openNdaDrawer = useCallback(
    (signer: DataRoomNdaSigner, signing: DataRoomNdaSigning) => {
      setSelectedSigner(signer);
      setSelectedSigning(signing);
      drawer?.setOpen(true);
    },
    [drawer],
  );

  const openNdaModal = useCallback(
    (signer: DataRoomNdaSigner) => {
      setSelectedSigner(signer);
      modal?.open();
    },
    [modal],
  );

  const updateSelectedSigner = useCallback(
    (signer: Partial<DataRoomNdaSigner>) => {
      updateSigner(signer);

      if (selectedSigner?._id !== signer._id) return;

      setSelectedSigner((prev) => ({ ...prev, ...signer }));
      setSelectedSigning((prev) =>
        signer.dataRoomNdaSignings.find(({ _id }) => _id === prev._id),
      );
    },
    [selectedSigner?._id, updateSigner],
  );

  const onSendReminder = useCallback(
    (signingId: string) => {
      updateSelectedSigner({
        ...selectedSigner,
        dataRoomNdaSignings: selectedSigner.dataRoomNdaSignings.map((signing) =>
          signing._id !== signingId
            ? signing
            : {
                ...signing,
                sentDate: new Date(),
                timeline: [
                  ...signing.timeline,
                  {
                    action: DATA_ROOM_NDA_SIGNING_TIMELINE_ACTION.REMINDER,
                    date: new Date(),
                    ...getSignerInfo(selectedSigner),
                  },
                ],
              },
        ),
      });
    },
    [selectedSigner, updateSelectedSigner],
  );

  const onDrawerClose = useCallback(() => {
    setSelectedSigner(null);
    setSelectedSigning(null);
  }, []);

  const onSigningSelect = useCallback(
    (signingId: string) => {
      if (!selectedSigner) return;

      const singing = selectedSigner.dataRoomNdaSignings.find(
        ({ _id }) => _id === signingId,
      );

      setSelectedSigning(singing);
      modal.close();
      drawer?.setOpen(true);
    },
    [selectedSigner, drawer, modal],
  );

  return (
    <DataRoomNdaTableContext.Provider value={{ openNdaDrawer, openNdaModal }}>
      {children}
      <DataRoomNdaSignerDrawer
        ref={drawerRef}
        dataRoomSigner={selectedSigner}
        dataRoomNdaSigning={selectedSigning}
        closeAction={onDrawerClose}
        onSendReminder={onSendReminder}
      />
      <DataRoomChooseNdaModal
        ref={modalRef}
        signings={selectedSigner?.dataRoomNdaSignings}
        onSigningSelect={onSigningSelect}
      />
    </DataRoomNdaTableContext.Provider>
  );
};

export default DataRoomNdaTableProvider;
